import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { FormModal } from '@/components/ui/form-modal';
import { useResourceSets } from 'components/IAP/hooks/resourceSetHooks';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import {
  useAttachResourceToRoster,
  useGetPositionsForIncident,
  useGetRosterForIncident,
} from 'hooks/incident/useRoster';
import {
  SearchableSelect,
  type SearchableSelectOption,
} from './SearchableSelect';

type AddResourceToRosterDialogProps = {
  show: boolean;
  onClose: () => void;
  isDraft?: boolean;
  /**
   * When set, pre-selects and locks the Reports To seat (e.g. from the
   * org-chart + menu).
   */
  defaultReportsToPositionId?: string | null;
};

const AddResourceToRosterDialog = ({
  show,
  onClose,
  isDraft = false,
  defaultReportsToPositionId = null,
}: AddResourceToRosterDialogProps) => {
  const incidentId = useActualIncidentId();

  const { data: resourceSets = [] } = useResourceSets();
  const { data: positions = [] } = useGetPositionsForIncident();
  const { data: rosterEntries = [] } = useGetRosterForIncident();

  const { mutate: attachResource, isPending } = useAttachResourceToRoster();

  const [selectedResource, setSelectedResource] =
    useState<SearchableSelectOption | null>(null);
  const [selectedPointOfContact, setSelectedPointOfContact] =
    useState<SearchableSelectOption | null>(null);
  const [selectedReportsTo, setSelectedReportsTo] =
    useState<SearchableSelectOption | null>(null);
  const [showValidation, setShowValidation] = useState(false);

  const resourceOptions = useMemo(() => {
    const alreadyOnRoster = new Set(
      positions
        .filter((position) => position.isResourcePosition)
        .map((position) => position.resourceSetId)
        .filter(Boolean)
    );

    return resourceSets
      .filter(
        (resourceSet) =>
          resourceSet.kind !== 'Personnel' &&
          !resourceSet.archived &&
          !alreadyOnRoster.has(resourceSet.resource_set_id)
      )
      .map((resourceSet) => ({
        value: resourceSet.resource_set_id,
        label: `${resourceSet.name}${resourceSet.kind ? ` — ${resourceSet.kind}` : ''}`,
      }));
  }, [resourceSets, positions]);

  const pointOfContactOptions = useMemo(
    () =>
      rosterEntries.map((entry) => ({
        value: entry.userId,
        label: entry.userName ?? entry.emailAddress ?? entry.userId,
      })),
    [rosterEntries]
  );

  const reportsToOptions = useMemo(
    () =>
      positions.map((position) => ({
        value: position.id,
        label: position.name,
      })),
    [positions]
  );

  const lockedReportsTo = useMemo(() => {
    if (!defaultReportsToPositionId) return null;
    return (
      reportsToOptions.find(
        (option) => option.value === defaultReportsToPositionId
      ) ?? null
    );
  }, [defaultReportsToPositionId, reportsToOptions]);

  const reportsToValue = lockedReportsTo ?? selectedReportsTo;

  const close = () => {
    setSelectedResource(null);
    setSelectedPointOfContact(null);
    setSelectedReportsTo(null);
    setShowValidation(false);
    onClose();
  };

  const onSubmit = () => {
    if (!selectedResource || !selectedPointOfContact || !reportsToValue) {
      setShowValidation(true);
      return;
    }

    attachResource(
      {
        incidentId,
        resourceSetId: String(selectedResource.value),
        pointOfContactUserId: String(selectedPointOfContact.value),
        reportsToPositionId: String(reportsToValue.value),
        isDraft,
      },
      {
        onSuccess: () => {
          toast.success('Resource added to roster');
          close();
        },
      }
    );
  };

  const missing = (selected: SearchableSelectOption | null) =>
    showValidation && !selected;

  return (
    <FormModal
      open={show}
      onOpenChange={(open) => {
        if (!open) close();
      }}
      title={`Add ${isDraft ? '(Draft) ' : ''}Resource`}
      formProps={{
        onSubmit: (event) => {
          event.preventDefault();
          onSubmit();
        },
      }}
      footer={
        <>
          <Button type="button" variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button type="submit" disabled={isPending}>
            Add Resource
          </Button>
        </>
      }
    >
      <div className="tw:space-y-4">
        <div>
          <SearchableSelect
            id="add-resource-resource"
            label="Resource"
            options={resourceOptions}
            value={selectedResource}
            onChange={setSelectedResource}
            placeholder="Select a resource"
            clearable
            invalid={missing(selectedResource)}
          />
          {missing(selectedResource) ? (
            <p className="tw:mt-1 tw:text-sm tw:text-destructive">
              A resource is required
            </p>
          ) : null}
        </div>

        <div>
          <SearchableSelect
            id="add-resource-point-of-contact"
            label="Point of Contact"
            options={pointOfContactOptions}
            value={selectedPointOfContact}
            onChange={setSelectedPointOfContact}
            placeholder="Select a point of contact"
            clearable
            invalid={missing(selectedPointOfContact)}
          />
          {missing(selectedPointOfContact) ? (
            <p className="tw:mt-1 tw:text-sm tw:text-destructive">
              A point of contact is required
            </p>
          ) : null}
        </div>

        <div>
          <SearchableSelect
            id="add-resource-reports-to"
            label="Reports To"
            options={reportsToOptions}
            value={reportsToValue}
            onChange={setSelectedReportsTo}
            placeholder="Select a position"
            clearable={!lockedReportsTo}
            disabled={!!lockedReportsTo}
            invalid={missing(reportsToValue)}
          />
          {missing(reportsToValue) ? (
            <p className="tw:mt-1 tw:text-sm tw:text-destructive">
              A reporting position is required
            </p>
          ) : null}
        </div>
      </div>
    </FormModal>
  );
};

export default AddResourceToRosterDialog;
